/**
 * Global ID utilities for GraphQL Relay-style IDs
 *
 * GraphQL uses base64-encoded global IDs in the format "Type:123" -> "VHlwZToxMjM="
 *
 * These functions are defensive - they handle:
 * - Base64-encoded IDs: "TGluazoxMjM=" -> 123
 * - Already-decoded IDs: "Link:123" -> 123
 * - Raw numeric strings: "123" -> 123
 * - Numbers passed through: 123 -> 123
 */

/**
 * Encode a numeric ID to a global ID
 * @example encodeGlobalId('Link', 123) -> "TGluazoxMjM="
 */
export function encodeGlobalId(type: string, id: number): string {
	return btoa(`${type}:${id}`);
}

/**
 * Decode a global ID to get the numeric ID (defensive - handles multiple formats)
 * @example decodeGlobalId("TGluazoxMjM=") -> 123
 * @example decodeGlobalId("Link:123") -> 123
 * @example decodeGlobalId("123") -> 123
 * @example decodeGlobalId(123) -> 123
 */
export function decodeGlobalId(globalId: string | number): number {
	// Already a number? Return it
	if (typeof globalId === 'number') {
		return globalId;
	}

	// Raw numeric string? Parse directly
	const asNumber = Number(globalId);
	if (!isNaN(asNumber) && String(asNumber) === globalId) {
		return asNumber;
	}

	// Contains colon? Already decoded format "Type:123"
	if (globalId.includes(':')) {
		const idStr = globalId.split(':')[1];
		return parseInt(idStr, 10);
	}

	// Try base64 decode
	try {
		const decoded = atob(globalId);
		if (decoded.includes(':')) {
			return parseInt(decoded.split(':')[1], 10);
		}
	} catch {
		// Not valid base64
	}

	// Last resort - try parsing as number
	return parseInt(globalId, 10);
}

/**
 * Decode a global ID and return both type and ID (defensive - handles multiple formats)
 * @example parseGlobalId("TGluazoxMjM=") -> { type: "Link", id: 123 }
 * @example parseGlobalId("Link:123") -> { type: "Link", id: 123 }
 */
export function parseGlobalId(globalId: string): { type: string; id: number } {
	// Contains colon? Already decoded format "Type:123"
	if (globalId.includes(':')) {
		const [type, idStr] = globalId.split(':');
		return { type, id: parseInt(idStr, 10) };
	}

	// Try base64 decode
	try {
		const decoded = atob(globalId);
		if (decoded.includes(':')) {
			const [type, idStr] = decoded.split(':');
			return { type, id: parseInt(idStr, 10) };
		}
	} catch {
		// Not valid base64
	}

	// Fallback - unknown type, try to parse as number
	return { type: 'Unknown', id: parseInt(globalId, 10) };
}
